import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import { TerritoryBoundary } from '../territory/typings'
import { setMapViewDialogOpen } from '../map/slice'

/**
 * The rule form, parked while its author goes to the map to draw (WWM-17508).
 *
 * Deliberately untyped here. The shape belongs to the assignment rule form, and a store slice
 * reaching into a component's model to name it would couple the two the wrong way round; this
 * slice only has to hand back what it was given. The rule page casts it on the way out.
 */
export type ParkedRuleForm = Record<string, unknown>

export type DraftedTerritory = {
  name: string
  boundary: TerritoryBoundary
}

export type TerritoryDrawState = {
  /**
   * Set while an admin is away drawing. Its presence is what tells the map to arm itself and
   * the rules page to reopen the form when they come back.
   */
  parkedRule: ParkedRuleForm | null
  /**
   * The condition being built when they left, so a mixed selection -- two existing territories
   * and a new one -- survives the trip.
   */
  parkedCondition: ParkedRuleForm | null
  /**
   * The shape they drew and named. NOT yet a territory: it has no sales team, and the server
   * requires one, so it is created when the rule is saved and the team is known. Nothing is
   * left behind if the rule is abandoned.
   */
  drafted: DraftedTerritory | null
  /**
   * True while they are on the map with the pen armed.
   *
   * Tracked separately from the two above rather than inferred from them, because cancelling a
   * drawing and finishing one both end with "off the map, rule form still parked" -- and if the
   * map re-armed itself whenever a rule was parked without a drawing, cancelling would be
   * impossible.
   */
  onMap: boolean
  /**
   * The SAVED territory being redrawn, when that is why they are on the map.
   *
   * The other direction of the same trip: drawing a new territory ends with a shape the rule
   * carries home, while redrawing one ends with a PUT on the map, because the territory already
   * has the name, team and owners an update needs. Null means they are drawing a new one.
   */
  redrawTerritoryId: number | null
  /**
   * WHICH rule was parked -- its id, or null for one being created.
   *
   * Without this the park is anonymous, and a leftover one is applied to whatever rule is
   * opened next: you click "terr one main" and see the territory from the rule you built
   * before it. Parked state belongs to one editing session and has to say which.
   */
  parkedRuleId: number | null
}

export const initialState: TerritoryDrawState = {
  parkedRule: null,
  parkedCondition: null,
  drafted: null,
  onMap: false,
  redrawTerritoryId: null,
  parkedRuleId: null,
}

const territoryDrawSlice = createSlice({
  name: 'territoryDraw',
  initialState,
  reducers: {
    /** Leaving the rule form for the map. */
    startTerritoryDrawForRule: (
      state,
      action: PayloadAction<{
        rule: ParkedRuleForm
        condition: ParkedRuleForm | null
        ruleId: number | null
      }>,
    ) => {
      state.parkedRule = action.payload.rule
      state.parkedCondition = action.payload.condition
      state.parkedRuleId = action.payload.ruleId
      state.drafted = null
      state.onMap = true
      state.redrawTerritoryId = null
    },

    /**
     * Leaving the rule form to reshape a territory it already uses.
     *
     * Nothing comes back in the rule: the update happens on the map, so this only has to get
     * them there and back to the form they were filling in.
     */
    startTerritoryRedrawForRule: (
      state,
      action: PayloadAction<{
        rule: ParkedRuleForm
        condition: ParkedRuleForm | null
        territoryId: number
        ruleId: number | null
      }>,
    ) => {
      state.parkedRule = action.payload.rule
      state.parkedCondition = action.payload.condition
      state.parkedRuleId = action.payload.ruleId
      state.drafted = null
      state.onMap = true
      state.redrawTerritoryId = action.payload.territoryId
    },
    /** The shape is drawn and named; the rule form takes it from here. */
    finishTerritoryDraw: (state, action: PayloadAction<DraftedTerritory>) => {
      state.drafted = action.payload
      state.onMap = false
      state.redrawTerritoryId = null
    },

    /** The redraw is saved. Nothing to carry back but the trip itself. */
    finishTerritoryRedraw: (state) => {
      state.onMap = false
      state.redrawTerritoryId = null
    },
    /**
     * Gave up on the drawing. The parked rule stays, so they land back on the form they were
     * filling in rather than an empty one -- abandoning a drawing is not abandoning the rule.
     */
    cancelTerritoryDraw: (state) => {
      state.drafted = null
      state.onMap = false
      state.redrawTerritoryId = null
    },
    /** The rule was saved, or its form closed. Nothing to come back to. */
    clearTerritoryDraw: () => initialState,
  },
  extraReducers: (builder) => {
    // Closing the map ends the trip, however it is closed.
    //
    // onMap was only ever lowered by this slice's own actions, every one of which is dispatched
    // from a button on the draw banner or its dialogs. The map dialog's ordinary close path --
    // the X, Escape, the backdrop, all of which go through setMapViewDialogOpen({open:false})
    // -- dispatched none of them, and there was no unmount cleanup, so onMap stayed true after
    // the map was gone. selectShouldResumeRule requires !onMap, so an admin who clicked "Draw a
    // new territory" on a rule and then closed the map never got their parked rule form back,
    // and the next time they opened the map it greeted them with a stale "Where does the
    // territory for '<rule>' go?" banner.
    //
    // Cancel semantics, not clear: the parked rule survives, because closing the map is
    // abandoning the DRAWING, not the rule they were writing. `drafted` survives for the same
    // reason -- a shape already drawn and named belongs to the rule form, and it has been
    // handed over by the time this can fire.
    builder.addCase(setMapViewDialogOpen, (state, action) => {
      if (action.payload.open) return

      state.onMap = false
      state.redrawTerritoryId = null
    })
  },
})

export const {
  startTerritoryDrawForRule,
  startTerritoryRedrawForRule,
  finishTerritoryDraw,
  finishTerritoryRedraw,
  cancelTerritoryDraw,
  clearTerritoryDraw,
} = territoryDrawSlice.actions

/** True while the map should be in "draw a territory for a rule" mode. */
export const selectTerritoryDrawOnMap = (state: StoreData) =>
  state.territoryDraw.onMap

/**
 * True when the rules page should reopen the form it parked -- they are back from the map,
 * whether they drew something or gave up.
 */
export const selectShouldResumeRule = (state: StoreData) =>
  state.territoryDraw.parkedRule !== null && !state.territoryDraw.onMap

export const selectParkedRule = (state: StoreData) =>
  state.territoryDraw.parkedRule
export const selectParkedCondition = (state: StoreData) =>
  state.territoryDraw.parkedCondition
export const selectDraftedTerritory = (state: StoreData) =>
  state.territoryDraw.drafted

/** The saved territory being reshaped, or null when a new one is being drawn. */
export const selectRedrawTerritoryId = (state: StoreData) =>
  state.territoryDraw.redrawTerritoryId

/** The rule the parked form belongs to; null means a rule that was being created. */
export const selectParkedRuleId = (state: StoreData) =>
  state.territoryDraw.parkedRuleId

export default territoryDrawSlice.reducer
